import React from 'react';
import { Box, Typography, Container, Grid, Button, Toolbar, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import backdrop from '../img/backdrop.png';
import devices from '../img/devices.png';
import iosBadge from "../img/ios-badge.svg";
import googlePlayBadge from "../img/googleplay-badge.png";
import { useUserData } from '../services/userDataService.jsx';
// tracking removed
import device1 from '../img/device-1.jpg';
import device2 from '../img/device-2.jpg';
import device3 from '../img/device-3.jpg';
import device4 from '../img/device-4.jpg';
import device5 from '../img/device-5.jpg';
import device6 from '../img/device-6.jpg';

// Streaming App Banner Component for subscribed users
const StreamingAppBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 4, 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid rgba(255, 0, 0, 0.3)',
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
    >
      <Grid container sx={{ alignItems: 'center' }}>
        <Grid item xs={12}>
          <Box sx={{ 
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 1, 
                fontSize: { xs: '1.4rem', sm: '1.6rem' },
                textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
              }}
            >
              Access Screamify Streaming
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 1.5,
                maxWidth: '600px'
              }}
            >
              You're subscribed! Download the Screamify app to start streaming horror movies, originals, and podcasts.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                mb: 3,
                lineHeight: 1.4,
                maxWidth: '600px'
              }}
            >
              Screamify is available on iOS, Android mobile, Roku, FireTV, AppleTV, and Android TV.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'center'
            }}>
              <Box 
                component="a"
                href="https://apps.apple.com/us/app/screamify/id6738323226"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Box 
                  component="img"
                  src={iosBadge}
                  alt="Download on the App Store"
                  sx={{
                    height: { xs: '40px', sm: '48px' },
                    cursor: 'pointer',
                    transition: 'transform 0.2s, filter 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      filter: 'brightness(1.1)'
                    }
                  }}
                />
              </Box>
              <Box 
                component="a"
                href="https://play.google.com/store/apps/details?id=com.screamify.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Box 
                  component="img"
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  sx={{
                    height: { xs: '40px', sm: '48px' },
                    cursor: 'pointer',
                    transition: 'transform 0.2s, filter 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      filter: 'brightness(1.1)'
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Simple top banner for non-subscribed users
const TopBanner = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleAdClick = () => {
    navigate('/subscribe');
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 4, 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid rgba(255, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(255, 0, 0, 0.3)',
        },
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
      onClick={handleAdClick}
    >
      <Grid container sx={{ alignItems: 'center' }}>
        <Grid item xs={12} md={8}>
          <Box sx={{ padding: { xs: '20px 20px 10px 20px', md: '30px 20px 20px 30px' } }}>
            <Box 
              component="img"
              src={devices}
              alt="Screamify on all devices"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ 
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            textAlign: { xs: 'center', md: 'left' }
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 2, 
                fontSize: { xs: '1.4rem', sm: '1.6rem' } 
              }}
            >
              Become a Member
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 2,
                lineHeight: 1.4
              }}
            >
              Access hand picked indie horror films, originals and podcasts.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                mb: 1.5,
                lineHeight: 1.4,
                fontStyle: 'italic'
              }}
            >
              To access Screamify streaming, download the Screamify app.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: '#ff0000', 
                '&:hover': { bgcolor: '#cc0000' },
                minWidth: { xs: '100%', sm: '150px' },
                alignSelf: { xs: 'center', md: 'flex-start' },
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(255, 0, 0, 0.3)',
                py: 1
              }}
            >
              Become a Member
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Free-tier banner
const FreeTierInfoBanner = () => {
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 4, 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid rgba(255, 0, 0, 0.3)',
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
    >
      <Grid container sx={{ alignItems: 'center' }}>
        <Grid item xs={12}>
          <Box sx={{ 
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 1.5, 
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
              }}
            >
              You're on the Free Tier
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 2,
                maxWidth: '500px',
                lineHeight: 1.4
              }}
            >
              To access Screamify Podcasts, download the iOS or Android app.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              <Box component="a" href="https://apps.apple.com/us/app/screamify/id6738323226" target="_blank" rel="noopener noreferrer">
                <Box component="img" src={iosBadge} alt="Download on the App Store" sx={{ height: { xs: '36px', sm: '42px' } }} />
              </Box>
              <Box component="a" href="https://play.google.com/store/apps/details?id=com.screamify.app&pcampaignid=web_share" target="_blank" rel="noopener noreferrer">
                <Box component="img" src={googlePlayBadge} alt="Get it on Google Play" sx={{ height: { xs: '36px', sm: '42px' } }} />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

function ScreamifyCentral() {
  const { userData } = useUserData();
  const isUserSubscribed = userData?.subscriptionStatus === 'active';
  const deviceImages = [
    { src: device1, alt: 'Android TV' },
    { src: device2, alt: 'Amazon Fire TV' },
    { src: device3, alt: 'Apple iOS' },
    { src: device4, alt: 'Android Mobile' },
    { src: device5, alt: 'Roku' },
    { src: device6, alt: 'Apple TV' },
  ];

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#000000',
        backgroundImage: `url(${backdrop})`,
        backgroundSize: '150%',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Header />
        <Toolbar />
        <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>

          {isUserSubscribed ? (
            <StreamingAppBanner />
          ) : (
            <>
              <TopBanner />
              <FreeTierInfoBanner />
            </>
          )}

          {/* Devices Gallery */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 2, textAlign: 'center', textShadow: '0 0 10px rgba(255, 255, 255, 0.15)' }}>
              Available on all your devices
            </Typography>
            <Grid container spacing={2}>
              {deviceImages.map((img, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      display: 'block',
                      border: '1px solid rgba(255, 0, 0, 0.15)',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.6)'
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
}

export default ScreamifyCentral;


